import { useEffect, useRef } from 'react';
import {
  CharlieDialog,
  CharlieDialogContent,
  CharlieButton,
  CharlieSeparator,
} from '@ver.id/libs.ui.charlie';
import { useTranslation } from '../../i18n/i18n';
import { TRUST_CENTER_LINKS } from '../../data/trustCenterLinks';

/**
 * HostedFaqModal — Modal dialog for FAQ topics.
 *
 * Figma screens:
 *   Desktop (Journey 5): https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=73-9330
 *   Mobile  (Journey 5): https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=194-52126
 */

export type FaqTopic =
  | 'whatAreWallets'
  | 'whyNotSupported'
  | 'howDataProtected'
  | 'howObtainData'
  | 'whyRedirection';

type Props = {
  topic: FaqTopic | null;
  onChangeTopic: (topic: FaqTopic) => void;
  onClose: () => void;
};

const relatedTopics: Record<FaqTopic, FaqTopic[]> = {
  whatAreWallets: ['howDataProtected', 'howObtainData'],
  whyNotSupported: ['whatAreWallets', 'howObtainData'],
  howDataProtected: ['whatAreWallets', 'whyRedirection'],
  howObtainData: ['whyNotSupported', 'whyRedirection'],
  whyRedirection: ['howDataProtected', 'whatAreWallets'],
};

/** Topics whose answer continues on a Ver.iD trust-center page, opened in a new tab. */
const externalLinks: Partial<Record<FaqTopic, string>> = {
  howDataProtected: TRUST_CENTER_LINKS.security,
};

export default function HostedFaqModal({
  topic,
  onChangeTopic,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const previousTopicRef = useRef<FaqTopic | null>(null);
  const currentTopicRef = useRef<FaqTopic | null>(null);

  useEffect(() => {
    if (topic && topic !== currentTopicRef.current) {
      previousTopicRef.current = currentTopicRef.current;
      currentTopicRef.current = topic;
    }
  }, [topic]);

  if (!topic) return null;

  const others = relatedTopics[topic];
  const externalLink = externalLinks[topic];
  const previousTopic = previousTopicRef.current;
  const related =
    previousTopic && previousTopic !== topic && !others.includes(previousTopic)
      ? [...others, previousTopic]
      : others;

  return (
    <CharlieDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <CharlieDialogContent
        header={
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-muted-foreground">
              {t('faq.helpQuestion')}
            </p>
            <h2 className="text-xl font-semibold text-foreground">
              {t(`faq.${topic}.title`)}
            </h2>
          </div>
        }
      >
        <div className="flex flex-col gap-4 max-h-[50vh] overflow-y-auto">
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {t(`faq.${topic}.body`)}
          </p>
          {externalLink && (
            <a
              href={externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="self-start text-sm text-muted-foreground underline hover:text-foreground transition-colors"
            >
              {t(`faq.${topic}.link`)}
            </a>
          )}

          <CharlieSeparator />

          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-foreground">
              {t('faq.moreHelp')}
            </p>
            <div className="flex flex-col gap-3">
              {related.map((other) => (
                <button
                  key={other}
                  type="button"
                  onClick={() => onChangeTopic(other)}
                  className="text-sm text-muted-foreground underline text-left hover:text-foreground transition-colors"
                >
                  {t(`faq.${other}.title`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <CharlieButton variant="outline" pill block onClick={onClose}>
          {t('faq.close')}
        </CharlieButton>
      </CharlieDialogContent>
    </CharlieDialog>
  );
}
